import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ShieldCheck } from "lucide-react";

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [{ data }, { data: c }] = await Promise.all([api.get("/packages"), api.get("/catalog")]);
    setPackages(data);
    setCatalog(c);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.nome) return toast.error("Nome obbligatorio");
    try {
      if (editing.id) await api.patch(`/packages/${editing.id}`, editing);
      else await api.post("/packages", editing);
      toast.success("Salvato");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error("Errore"); }
  };
  const del = async (id) => {
    if (!confirm("Eliminare il pacchetto?")) return;
    await api.delete(`/packages/${id}`); toast.success("Eliminato"); load();
  };

  const addItem = () => setEditing({ ...editing, items: [...(editing.items || []), { catalog_item_id: "", quantita: 1, note: "" }] });
  const updItem = (i, k, v) => setEditing({ ...editing, items: editing.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const rmItem = (i) => setEditing({ ...editing, items: editing.items.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Configurazione</div>
          <h1 className="font-display text-3xl font-bold text-[#0F2C59]">Pacchetti</h1>
          <p className="text-slate-500">Pacchetti standard per creare rapidamente offerte pre-configurate.</p>
        </div>
        <Button onClick={() => { setEditing({ nome: "", descrizione: "", items: [], attivo: true }); setOpen(true); }} className="bg-[#0F2C59] hover:bg-[#173F7A]" data-testid="new-pkg-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo pacchetto
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.map((p) => (
          <Card key={p.id} className="p-5">
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-md bg-[#FEF9C3] flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#EAB308]" />
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={() => { setEditing({ ...p }); setOpen(true); }}
                  className="p-1.5 rounded border border-slate-200 bg-white hover:bg-blue-50 hover:border-[#0F2C59] text-[#0F2C59] transition-colors"
                  title="Modifica pacchetto"
                  data-testid={`edit-pkg-${p.id}`}
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => del(p.id)}
                  className="p-1.5 rounded border border-slate-200 bg-white hover:bg-red-50 hover:border-red-300 text-red-500 transition-colors"
                  title="Elimina pacchetto"
                  data-testid={`del-pkg-${p.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="font-display font-semibold text-[#0F2C59]">{p.nome}</div>
            <div className="text-sm text-slate-500 mt-1">{p.descrizione}</div>
            <div className="text-xs mt-3 text-slate-600">{p.items?.length || 0} voci</div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica pacchetto" : "Nuovo pacchetto"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Nome</Label><Input value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} className="mt-1.5" data-testid="pkg-nome" /></div>
              <div><Label>Descrizione</Label><Textarea rows={2} value={editing.descrizione} onChange={(e) => setEditing({ ...editing, descrizione: e.target.value })} className="mt-1.5" /></div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Voci del pacchetto</Label>
                  <Button variant="outline" size="sm" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Aggiungi</Button>
                </div>
                <div className="space-y-2">
                  {(editing.items || []).map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Select value={it.catalog_item_id} onValueChange={(v) => updItem(i, "catalog_item_id", v)}>
                        <SelectTrigger className="col-span-8"><SelectValue placeholder="Voce di listino…" /></SelectTrigger>
                        <SelectContent>
                          {catalog.map((c) => (<SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Input type="number" className="col-span-3" value={it.quantita} onChange={(e) => updItem(i, "quantita", Number(e.target.value))} placeholder="Qtà" />
                      <button onClick={() => rmItem(i)} className="col-span-1 p-1.5 hover:bg-red-50 text-red-500 rounded justify-center flex"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2C59]" data-testid="save-pkg-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
